import React, { useMemo } from "react";
import dayjs from "dayjs";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    DefaultLegendContent,
} from "recharts";

export type AdCostBreakdownYoYRow = {
    month: string; // "YYYY/MM"

    // 前期（前年同月）
    rakuten_prev: number; // 楽天広告（= ec など）
    rpp_prev: number;
    rppexp_prev: number;
    cpa_prev: number;
    cpnadv_prev: number;
    tda_prev: number;
    tdaexp_prev: number;
    deal_prev: number;

    // 今期
    rakuten_cur: number;
    rpp_cur: number;
    rppexp_cur: number;
    cpa_cur: number;
    cpnadv_cur: number;
    tda_cur: number;
    tdaexp_cur: number;
    deal_cur: number;
};

type Props = {
    data: AdCostBreakdownYoYRow[];
    loading?: boolean;
    emptyLabel?: string;
    title?: string;

    // ✅ OFF: 今期のみ / ON: 前期+今期
    showCompare?: boolean;
    onChangeShowCompare?: (v: boolean) => void;
    compareLabel?: string;
};

type ChartRow = {
    x: string; // "YYYY/MM|前期" etc
    rakuten: number;
    rpp: number;
    rppexp: number;
    cpa: number;
    cpnadv: number;
    tda: number;
    tdaexp: number;
    deal: number;
};

const LEGEND_ORDER = ["rakuten", "rpp", "rppexp", "cpa", "cpnadv", "tda", "tdaexp", "deal"];

function fmtCompactYen(v: number) {
    const n = Number(v || 0);
    const a = Math.abs(n);
    if (a >= 1e8) return `${(n / 1e8).toFixed(1)}億`;
    if (a >= 1e4) return `${(n / 1e4).toFixed(1)}万`;
    return `${Math.round(n).toLocaleString("ja-JP")}`;
}
function fmtYen(v: number) {
    return `${Math.round(Number(v || 0)).toLocaleString("ja-JP")}円`;
}

function sumRow(r: any) {
    return (
        (Number(r?.rakuten ?? 0) || 0) +
        (Number(r?.rpp ?? 0) || 0) +
        (Number(r?.rppexp ?? 0) || 0) +
        (Number(r?.cpa ?? 0) || 0) +
        (Number(r?.cpnadv ?? 0) || 0) +
        (Number(r?.tda ?? 0) || 0) +
        (Number(r?.tdaexp ?? 0) || 0) +
        (Number(r?.deal ?? 0) || 0)
    );
}

export default function AdCostBreakdownYoYChart({
    data,
    loading = false,
    emptyLabel = "データがありません",
    title = "広告費内訳（楽天広告・RPP・RPP-EXP・CPA・クーポンアドバンス・TDA・TDA-EXP・DEAL）",
    showCompare = false,
    onChangeShowCompare,
    compareLabel = "前期比較（前年同月）",
}: Props) {
    const hasData = Array.isArray(data) && data.length > 0;

    const BASE = {
        blue: "#5C97E3",
        yellow: "#FED561",
        orange: "#FEB072",
        red: "#FB7772",
        cyan: "#5DBEE9",
    };

    const MIX = {
        blueCyan: "#5CAAE6", // blue + cyan
        yellowOrange: "#FEC26A", // yellow + orange
        orangeRed: "#FC9472", // orange + red
    };
    const ADD = {
        deepBlue: "#2F6FE6", // ベース青を締めるロイヤルブルー
        deepCyan: "#00A6D6", // シアンを締めるオーシャン系
        indigo: "#6F5CF0", // 青系の延長にあるインディゴ
    };
    const DARK = {
        blue: "#4571AA",
        red: "#BC5956",
        cyan: "#468EAF",
        // 必要ならさらに：
        orange: "#BE8456",
        yellow: "#BEA049",
    };

    const COLORS_ADS = {
        rakuten: BASE.cyan,
        rpp: BASE.red,
        rppexp: BASE.orange,
        cpa: BASE.yellow,
        cpnadv: BASE.blue,
        tda: ADD.deepBlue,
        tdaexp: ADD.deepCyan,
        deal: ADD.indigo,
    };


    // ✅ month+prev/cur を「前期行」「今期行」に展開（販促費内訳と同じ）
    const chartData: ChartRow[] = useMemo(() => {
        const out: ChartRow[] = [];
        for (const r of data || []) {
            const m = r.month;

            const prevM = (() => {
                const ym = String(m).replace(/\//g, "-"); // "YYYY-MM"
                const d = dayjs(ym + "-01");
                return d.isValid() ? d.subtract(1, "year").format("YYYY/MM") : String(m);
            })();

            if (showCompare) {
                out.push({
                    x: `${prevM}|前期`,
                    rakuten: Number(r.rakuten_prev ?? 0) || 0,
                    rpp: Number(r.rpp_prev ?? 0) || 0,
                    rppexp: Number(r.rppexp_prev ?? 0) || 0,
                    cpa: Number(r.cpa_prev ?? 0) || 0,
                    cpnadv: Number(r.cpnadv_prev ?? 0) || 0,
                    tda: Number(r.tda_prev ?? 0) || 0,
                    tdaexp: Number(r.tdaexp_prev ?? 0) || 0,
                    deal: Number(r.deal_prev ?? 0) || 0,
                });
            }

            out.push({
                x: `${m}|今期`,
                rakuten: Number(r.rakuten_cur ?? 0) || 0,
                rpp: Number(r.rpp_cur ?? 0) || 0,
                rppexp: Number(r.rppexp_cur ?? 0) || 0,
                cpa: Number(r.cpa_cur ?? 0) || 0,
                cpnadv: Number(r.cpnadv_cur ?? 0) || 0,
                tda: Number(r.tda_cur ?? 0) || 0,
                tdaexp: Number(r.tdaexp_cur ?? 0) || 0,
                deal: Number(r.deal_cur ?? 0) || 0,
            });
        }
        return out;
    }, [data, showCompare]);

    // ✅ X軸：各棒の下に年月を必ず表示（販促費内訳と同じ）
    const MultiLineTick = (props: any) => {
        const { x, y, payload } = props;
        const raw = String(payload?.value ?? "");
        const [m, p] = raw.split("|");
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} textAnchor="end" fill="#666" fontSize={11} transform="rotate(-45)">
                    {m}
                </text>
            </g>
        );
    };

    return (
        <div className="rounded-2xl border bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-xs font-semibold text-slate-800">{title}</div>

                {onChangeShowCompare && (
                    <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                        <input
                            type="checkbox"
                            checked={showCompare}
                            onChange={(e) => onChangeShowCompare(e.target.checked)}
                        />
                        {compareLabel}
                    </label>
                )}
            </div>

            {!hasData ? (
                <div className="text-sm text-slate-500">
                    {loading ? "読み込み中..." : emptyLabel}
                </div>
            ) : (
                <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                        <BarChart
                            key={showCompare ? "cmp-on" : "cmp-off"}
                            data={chartData}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            barCategoryGap={14}
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="x"
                                tickMargin={12}
                                tick={<MultiLineTick />}
                                height={64}
                                interval={0}
                                minTickGap={16}
                                padding={{ left: 30, right: 40 }}
                            />

                            <YAxis
                                tickFormatter={(v) => fmtCompactYen(Number(v))}
                                width={56}
                                tick={{ fontSize: 11 }}
                            />

                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload || !payload.length) return null;

                                    const row: any = payload?.[0]?.payload ?? {};
                                    const raw = String(label ?? "");
                                    const [m, p] = raw.split("|");
                                    const total = sumRow(row);

                                    return (
                                        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow">
                                            <div className="mb-1 font-semibold">
                                                {m}（{p}）
                                            </div>
                                            <div className="space-y-1 text-slate-700">
                                                <div className="font-semibold">合計: {fmtYen(total)}</div>
                                                <div>楽天広告: {fmtYen(row.rakuten)}</div>
                                                <div>RPP広告: {fmtYen(row.rpp)}</div>
                                                <div>RPP-EXP広告: {fmtYen(row.rppexp)}</div>
                                                <div>楽天CPA広告: {fmtYen(row.cpa)}</div>
                                                <div>クーポンアドバンス広告: {fmtYen(row.cpnadv)}</div>
                                                <div>TDA広告: {fmtYen(row.tda)}</div>
                                                <div>TDA-EXP広告: {fmtYen(row.tdaexp)}</div>
                                                <div>DEAL成果報酬: {fmtYen(row.deal)}</div>
                                            </div>
                                        </div>
                                    );
                                }}
                            />

                            {/* ✅ 積み上げ（常に stackId="a"） */}
                            <Bar dataKey="rakuten" stackId="a" name="楽天広告" fill={COLORS_ADS.rakuten} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="rpp" stackId="a" name="RPP広告" fill={COLORS_ADS.rpp} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="rppexp" stackId="a" name="RPP-EXP広告" fill={COLORS_ADS.rppexp} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="cpa" stackId="a" name="楽天CPA広告" fill={COLORS_ADS.cpa} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="cpnadv" stackId="a" name="クーポンアドバンス広告" fill={COLORS_ADS.cpnadv} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="tda" stackId="a" name="TDA広告" fill={COLORS_ADS.tda} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="tdaexp" stackId="a" name="TDA-EXP広告" fill={COLORS_ADS.tdaexp} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="deal" stackId="a" name="DEAL成果報酬" fill={COLORS_ADS.deal} barSize={28} isAnimationActive={false} />

                            <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                content={(props: any) => {
                                    const payload = props?.payload || [];
                                    const sorted = [...payload].sort((a: any, b: any) => {
                                        const ai = LEGEND_ORDER.indexOf(a.dataKey);
                                        const bi = LEGEND_ORDER.indexOf(b.dataKey);
                                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                    });
                                    return <DefaultLegendContent {...props} payload={sorted} />;
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
